/////////////////////////////////////////////////////////////////
/////////      CompetitionApi     ///////////////////////////////
/////////////////////////////////////////////////////////////////

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { Response, requestDELETE, requestGET, requestPOST, requestPUT } from "../HttpRequestHelpers";
import { Competition_GET } from "../dto/Competition_GET";
import { Competition_POST_PUT } from "../dto/Competition_POST_PUT";
import { Discipline_GET } from "../dto/Discipline_GET";
import { Discipline_POST_PUT } from "../dto/Discipline_POST_PUT";
import { PriceCalculationDto_GET } from "../dto/PriceCalculationDto_GET";
import { DisciplineSignupDto_POST_PUT, SignupRequest} from "../dto/SignupRequest_POST";
import { SignupResponseDto } from "../dto/SignupResponseDtos";
import { StartlistDto, StartlistExtendedDto, StartlistExtendedRow, StartlistRow } from "../dto/StartlistDto";
import { DiscountDto_GET, DiscountDto_PUT } from "../dto/DiscountDtos";
import { StartnumberSequencesDto } from "../dto/StartnumberSequence_GET_PUT";
import { completeEmailLogin } from "../../Session/SessionApi";
import { ResultListDto } from "./ResultClient";
import { capitalizeFirstLetter } from "../../Components/Event/Results/BackendResults";
import { birthDateToString } from "../../Components/Event/Signup/Step2SelectParticipants";


export const CompetitionClient = {
    getCompetitions: async () => {
        //GET .../competitions/
        return await requestGET<Competition_GET[]>(
        `/api/competitions`
        );
    },

    getCompetition: async (tag: string) => {
        //GET .../competition/{tag}
        return await requestGET<Competition_GET>(
        `/api/competition/${tag}`
        );
    },

    admin : {
        getDeletedCompetitions: async () => {
            // GET .../competitions/deleted
            return await requestGET<Competition_GET[]>(
            `/api/competitions/deleted`
            );
        },
        getAllCompetitions: async () : Promise<Response<Competition_GET[]>> => {
            const disciplines = await CompetitionClient.getCompetitions();
            const deletedCompetitions = await CompetitionClient.admin.getDeletedCompetitions();

            return {body: [...(disciplines.body ?? []), ...(deletedCompetitions.body ?? [])], status: 200};
        },
        postCompetition: async (body: Competition_POST_PUT) => {
            // POST .../competition
            return await requestPOST<Competition_GET>(
            `/api/competition`,
            body
            );
        },
        putCompetition: async (competitionId: number, body: Competition_POST_PUT) => {
            // PUT .../competition/{id}
            return await requestPUT<Competition_GET>(
            `/api/competition/${competitionId}`,
            body
            );
        },
        putStartnumberAssignment: async (competitionId: number) => {
            return axios.put(`/api/admin/competition/${competitionId}/startnumbers/assign`, undefined);
        },
        deleteCompetition: async (competitionId: number) => {
            // DELETE .../competition/{id}
            return await requestDELETE<null>(
            `/api/competition/${competitionId}`,
            );
        },
        restoreCompetition: async (competitionId: number) => {
            // PUT .../competition/{id}/restore
            return await requestPUT<undefined>(
                `/api/competition/${competitionId}`,
                undefined
            );
        },
        getDiscounts: async (competitionId: number) => {
            // GET .../competition/{id}/discounts
            return await requestGET<DiscountDto_GET>(
                `/api/competition/${competitionId}/discounts`
            );
        },
        putDiscount: async (competitionId: number, body: DiscountDto_PUT) => {
            // PUT .../competition/{id}/discount
            return await requestPUT<DiscountDto_GET>(
                `/api/competition/${competitionId}/discount`,
                body
            );
        }

    }
}

export const DisciplineClient = {
    getDisciplines: async (competitionTag: string) => {
        return await requestGET<Discipline_GET[]>(
        `/api/competition/${competitionTag}/disciplines`
        );
    },

    admin : {
        getDeletedDisciplines: async (competitionId: number) => {
            return await requestGET<Discipline_GET[]>(
            `/api/competition/${competitionId}/disciplines/deleted`
            );
        },

        getAllDisciplines: async (competitionId: number, competitionTag: string) => {
            const disciplines = await DisciplineClient.getDisciplines(competitionTag);
            const deletedDisciplines = await DisciplineClient.admin.getDeletedDisciplines(competitionId);
            return [...(disciplines.body ?? []), ...(deletedDisciplines.body ?? [])];
        },

        postDiscipline: async (competitionId: number, body: Discipline_POST_PUT) => {
            // POST .../competition/{competitionId}/discipline
            return await requestPOST<Discipline_GET>(
                `/api/competition/${competitionId}/discipline`,
                body
            );
        },

        putDiscipline: async (disciplineId: number, body: Discipline_POST_PUT) => {
            // PUT .../competition/{competitionId}/discipline/{id}
            return await requestPUT<Discipline_GET>(
                `/api/discipline/${disciplineId}`,
                body
            );
        },

        deleteDiscipline: async (disciplineId: number) => {
            // DELETE .../discipline/{id}
            return await requestDELETE<undefined>(
                `/api/discipline/${disciplineId}`
            );
        },

        restoreDiscipline: async (disciplineId: number) => {
            // PUT .../discipline/{disciplineId}/restore
            return await requestPUT<undefined>(
                `/api/discipline/${disciplineId}/restore`,
                undefined
            );
        },

        getStartnumbers: async (competitionTag: string) => {
            return await requestGET<StartnumberSequencesDto[]>(
                `/api/competition/${competitionTag}/startnumbers`,
                undefined
            );
        },

        putStartnumbers: async (competitionTag: string, startnrData: StartnumberSequencesDto[]) => {
            return await requestPUT<undefined>(
                `/api/competition/${competitionTag}/startnumbers/config`,
                startnrData
            );
        },

    }
}

/////////////////////////////////////////////////////////////////
/////////      SignupApi      ///////////////////////////////////
/////////////////////////////////////////////////////////////////

export const SignupClient = {

    admin : {
        pushSignupRequest: async (competitionTag : string, body: DisciplineSignupDto_POST_PUT, email: string, cost : number) => {
                return await requestPOST<SignupResponseDto>(
                    `/api/admin/competition/${competitionTag}/signup?email=${email}&cost=${cost}`,
                    body
                );
        },
        deleteSignup: async (signupId: number) => {
            return axios.delete(`/api/admin/competition/signup?signupId=${signupId}`);
        }, 
        restoreDeletedSignup: async (signupId: number) => {
            return axios.put(`/api/admin/competition/signup/restore?signupId=${signupId}`);
        }, 
        assignStartnumbersWhereMissing: async (competitionId: number) => {
            return await requestPUT<{}>(`/api/admin/competition/${competitionId}/startnumbers/assign`, undefined);
        }
    },

    changeGroupName: async (competitionTag: string, oldName: string, newName: string, paymentId: number) => {
        return axios.put(`/api/competition/${competitionTag}/group-name/update?new=${newName}&old=${oldName}&paymentid=${paymentId}`)
    },

    putSignupUpdateRequest: async (competitionTag: string, signupId: number, body: SignupRequest) => {
        return axios.put(`/api/competition/${competitionTag}/signup/update/${signupId}`, body);
    },

    getExtendedStartlist: async (competitionTag: string | null) => {
        if (competitionTag == null) {
            return [] as Array<StartlistExtendedRow>;
        }

        return await axios.get(`/api/competition/${competitionTag}/startlist/extended`)
        .then((response: AxiosResponse<StartlistExtendedDto>) => {
            return response.data.rows;
        })
    },


    calculatePrise: async (competitionTag: String, request : DisciplineSignupDto_POST_PUT) => {
        request.signups.forEach(s => {
            if (s.fullBirthDate != null) {
                s.fullBirthYear = s.fullBirthDate?.getFullYear();
                if (s.socialSecurityNumber?.match(/^\d{4}$/)) {
                    s.socialSecurityNumber = birthDateToString(s.fullBirthDate) + "-" + s.socialSecurityNumber;
                }
                else if (!s.socialSecurityNumber?.match(/^\d{8}-[Xx0-9]{4}$/)) {
                    s.socialSecurityNumber = birthDateToString(s.fullBirthDate) + "-XXXX";
                }
            }
            delete s.fullBirthDate;
        });

        return await requestPOST<PriceCalculationDto_GET>(
            `/api/competition/${competitionTag}/signup/cost`,
            request
        );
    },

    initiateEmailVerification: async (email : string) => {
            return await requestGET<{c2 : string, timestamp: number}>(
                `/api/user/tpc/${email}`
            );
    },

    developerFakeSigninAsOtherPerson: async (email : string) => {
            return await requestGET<{c1: string, c2 : string, timestamp: number}>(
                `/api/login/impersonate/${email}`
            ).then((response1) => {
                return completeEmailLogin(email, response1.body!.c1, response1.body!.c2, response1.body!.timestamp, true);
            });
    },

    completeSignIn: async (r : string, email : string, c1c2: string) => {
            return await requestGET<string>(
                `/api/auth/authentication/email?r=${r}&email=${email}&c=${c1c2}`
            );
    },


    pushSignupRequest: async (competitionTag : string, body: DisciplineSignupDto_POST_PUT, email: string, requestedPrice: number | null | undefined, code1?: string, code2?: string, timestamp?: number) => {
        body.signups.forEach(s => {
            if (s.fullBirthDate != null) {
                s.fullBirthYear = s.fullBirthDate?.getFullYear();
                if (s.socialSecurityNumber?.match(/^\d{4}$/)) {
                    s.socialSecurityNumber = birthDateToString(s.fullBirthDate) + "-" + s.socialSecurityNumber;
                }
                else if (!s.socialSecurityNumber?.match(/^\d{8}-[Xx0-9]{4}$/)) {
                    s.socialSecurityNumber = birthDateToString(s.fullBirthDate) + "-XXXX";
                }
            }
            delete s.fullBirthDate;
        });

        if (code1 == null || code2 == null || timestamp == null) {
            const priceSuggestionString = requestedPrice != null ? `&suggestedprice=${requestedPrice}` : "";
            return await requestPOST<SignupResponseDto>(
                `/api/competition/${competitionTag}/signup?email=${email}${priceSuggestionString}`,
                body
            );
        }
        else {
            const priceSuggestionString = requestedPrice != null ? `?suggestedprice=${requestedPrice}` : "";
            return await requestPOST<SignupResponseDto>(
                `/api/competition/${competitionTag}/signup${priceSuggestionString}`,
                body, 
                {
                    "X-Email-Header": email, 
                    "X-Email-Verify-C1-Header": code1,
                    "X-Email-Verify-C2-Header": code2,
                    "X-Email-Verify-Timestamp":timestamp
                }
            );
                //?c=${code}&r=${encodeURIComponent(r)}&email=${encodeURIComponent(email)}`,
        }
    },

    getStartlist: async (competitionTag: string) => {
            return await requestGET<StartlistDto>(
                `/api/competition/${competitionTag}/startlist`
            );
    },

    fetchQRCodeFromBackend: async (swishToken:string) => {
        try {
            const response = await axios.get(`/api/swish/qrcode`, {
            params: {
                'swish-token': swishToken
            },
            responseType: 'text'
            });

            // Assuming the response is the Base64-encoded image data
            return response.data;
        } catch (error) {
            console.error('Error fetching QR code from backend:', error);
            throw error;
        }
    },

    checkPaymentStatus: async (competitionTag: string) => {
        return await requestGET<{paymentStatusTag: string, status: string}>(
            `/api/payment/swish/status/${competitionTag}`
        );
    },
}

async function getResultsFromDb(competitionTag?: string) {
    if (competitionTag == null) {
        return Promise.resolve({status: 404} as {
            status: number; 
            body?: ResultListDto[];
        });
    }
    return await requestGET<Array<ResultListDto>>(`/api/competition/${competitionTag}/results`);
}

async function getOldResultlistCompetitionTags(competitionTag: string) {
    if (competitionTag == null) {
        return [];
    }
    return await requestGET<Array<string>>(`/api/competition/${competitionTag}/old/tags`).then(r => r.body || []);
}


async function getStaticFileResults(competitionTag: string) {
    return requestGET<Array<ResultListDto>>(
        `/media/results/${competitionTag}.json`
    );
}



function sortAndSanitizeResultLists(resultDto: ResultListDto[] | undefined) {
    const EXTREME_FINISH_TIME = new Date().getTime() + 31536000000;
    const EXTREME_START_TIME = 0;

    if (resultDto == null) {
        return [] as Array<ResultListDto>;
    }

    resultDto.forEach(resultList => {
        resultList.results.sort((a, b) => {
            const aCompareTime = (a.finishTime ?? EXTREME_FINISH_TIME) - (a.startTime ?? EXTREME_START_TIME);
            const bCompareTime = (b.finishTime ?? EXTREME_FINISH_TIME) - (b.startTime ?? EXTREME_START_TIME);
            return aCompareTime - bCompareTime;
        });

        resultList.results.forEach(r => {
            r.firstname = capitalizeFirstLetter(r.firstname);
            r.lastname = capitalizeFirstLetter(r.lastname);
            r.clubOrTeam = capitalizeFirstLetter(r.clubOrTeam || "");
        });
    });
    return resultDto;
}

export const ResultClient = {
    getResults: async (competitionTag: string) => {
        const currentCompetitionResults = await getResultsFromDb(competitionTag);
        const allResults = [{competitionTag, results: sortAndSanitizeResultLists(currentCompetitionResults.body)}];

        const oldCompetitionTags : Array<string> = await getOldResultlistCompetitionTags(competitionTag);

        for (let i = 0; i < oldCompetitionTags.length; i++) {
            const tag = oldCompetitionTags.at(i);
            if (tag != null) {
                const response = await getStaticFileResults(tag);
                if (response.status === 200 && response.body) {
                    allResults.push({competitionTag: tag, results: sortAndSanitizeResultLists(response.body)} as ResultsResponse)
                }
            }
        }

        return allResults;
    },
/*
    getStatslopp2024Results: async (competitionTag: string) => {
        return await requestGET<Result[]>(
            `/api/competition/${competitionTag}/results`
        );
    },
    getStatslopp2023Results: async (competitionTag: string) => {
        return await requestGET<Result[]>(
            "/media/old.json"
        );
    },
    */
}
type ResultsResponse = {competitionTag: string, results: Array<ResultListDto>};
export type AllResultsResponse = Array<ResultsResponse>;


export interface SwishQrRequestDto {
    token: string,
    format: string, 
    size: number,
    border: number
}



export function randomStringGenerator() {
    const validChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const length = 10;
    let result = "";
    for (let i = 0; i < length; i++) {
        result += validChars.charAt(Math.floor(Math.random() * validChars.length));
    }
    return result;
}